/**
 * React adapter for global-store
 * Provides Redux store with automatic localStorage persistence
 */

import { configureStore } from "@reduxjs/toolkit";
import authReducer from "../../slices/auth.slice";
import fprReducer from "../../slices/fpr.slice";
import languageReducer from "../../slices/language.slice";
import { LocalStorageStrategy } from "../../persistence";

const STORAGE_KEY = "bfl_global_store";
const storage = new LocalStorageStrategy();

// Define the store shape type
type StoreState = {
  auth: ReturnType<typeof authReducer>;
  fpr: ReturnType<typeof fprReducer>;
  language: ReturnType<typeof languageReducer>;
};

const defaultFprState: ReturnType<typeof fprReducer> = fprReducer(undefined, {
  type: "@@INIT",
});

// Load persisted state from localStorage on initialization
function loadPersistedState(): StoreState | undefined {
  if (typeof window === "undefined") return undefined;

  const persisted = storage.get<StoreState>(STORAGE_KEY);

  if (persisted) {
    console.log("[GlobalStore] Hydrated state from localStorage");
    // Fall back to the slice's initial state if auth is missing/corrupted in localStorage,
    // preventing a runtime throw when spreading undefined/null.
    const persistedAuth = persisted.auth ?? authReducer(undefined, { type: "@@INIT" });
    return {
      auth: {
        ...persistedAuth,
        // Never restore modal visibility from persistence — it is UI-only state.
        // If it was persisted as true (e.g. user navigated away before the debounce
        // could write the closed state), it would incorrectly re-open the OTP modal
        // on the next page load.
        isCommonSsoModalVisible: false,
      },
      fpr: defaultFprState,
      language: persisted.language ?? languageReducer(undefined, { type: "@@INIT" }),
    };
  }

  return undefined;
}

export const store = configureStore({
  reducer: {
    auth: authReducer,
    fpr: fprReducer,
    language: languageReducer,
  },
  preloadedState: loadPersistedState(),
  devTools: process.env.NODE_ENV !== "production", // Disable in production to reduce TBT
});

// Throttle localStorage persistence to prevent blocking on every dispatch
// Queue writes and batch them every 1 second instead of synchronous writes
let persistTimeoutId: NodeJS.Timeout | null = null;
let hasPendingWrite = false;

store.subscribe(() => {
  hasPendingWrite = true;

  if (persistTimeoutId) return; // Already scheduled

  persistTimeoutId = setTimeout(() => {
    if (hasPendingWrite) {
      const state = store.getState();
      storage.set(STORAGE_KEY, state);
      hasPendingWrite = false;
    }
    persistTimeoutId = null;
  }, 1000); // Debounce writes to 1 second intervals
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
